import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import MaterialIcon from '@/components/ui/MaterialIcon';
import AppShell from '@/components/layout/AppShell';
import claimService from '@/services/claimService';
import benefitService from '@/services/benefitService';
import CoverageBanner from '@/components/coverage/CoverageBanner';
import { useAuthStore } from '@/store/authStore';
import { ClaimStatus } from '@/types';
import { formatCurrency } from '@/utils/formatters';

interface QuickActionProps {
  icon: string;
  title: string;
  subtitle: string;
  onClick: () => void;
  variant?: 'primary' | 'default';
  iconBg?: string;
}

const QuickAction: React.FC<QuickActionProps> = ({
  icon,
  title,
  subtitle,
  onClick,
  variant = 'default',
  iconBg
}) => {
  if (variant === 'primary') {
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex flex-col items-start gap-3 p-4 rounded-xl bg-primary text-[#102216] shadow-md hover:brightness-105 transition-all w-full text-left group"
      >
        <div className="bg-white/30 rounded-lg p-2 backdrop-blur-sm group-hover:bg-white/40 transition-colors">
          <MaterialIcon icon={icon} style={{ fontSize: 24 }} />
        </div>
        <div>
          <span className="block font-bold text-base">{title}</span>
          <span className="block text-xs opacity-80">{subtitle}</span>
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-start gap-3 p-4 rounded-xl bg-surface-light dark:bg-surface-dark border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md transition-all w-full text-left group"
    >
      <div className={`${iconBg} rounded-lg p-2 group-hover:scale-105 transition-transform`}>
        <MaterialIcon icon={icon} style={{ fontSize: 24 }} />
      </div>
      <div>
        <span className="block font-bold text-[#111813] dark:text-white text-base">{title}</span>
        <span className="block text-xs text-gray-500 dark:text-gray-400">{subtitle}</span>
      </div>
    </button>
  );
};

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const { data: benefitsData } = useQuery({
    queryKey: ['benefits'],
    queryFn: () => benefitService.getUserBenefits(),
    enabled: !!user?.id,
  });
  const coverageStatus = benefitsData?.status ?? 'active';

  const { data: recentClaims } = useQuery({
    queryKey: ['claims', 'recent'],
    queryFn: () => claimService.getClaims({}),
    select: (claims) =>
      [...claims]
        .filter((c) => c.status === ClaimStatus.PAID || c.status === ClaimStatus.APPROVED)
        .sort((a, b) =>
          new Date(b.reimbursementDate ?? b.updatedAt).getTime() -
          new Date(a.reimbursementDate ?? a.updatedAt).getTime()
        ),
  });
  const lastReimbursement = recentClaims?.[0];

  const contractName = user?.contract?.name ?? 'Contrat d\'assurance';
  const contractEnd = user?.contract?.endDate
    ? new Date(user.contract.endDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;
  const isCoverageActive = user?.contract?.status === 'active' || !user?.contract?.status;

  const quickActions = [
    {
      icon: 'id_card',
      title: 'Ma Carte',
      subtitle: 'Tiers Payant',
      variant: 'primary' as const,
      onClick: () => navigate('/card'),
    },
    {
      icon: 'location_on',
      title: 'Prestataires',
      subtitle: 'Trouver un médecin',
      iconBg: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      onClick: () => navigate('/providers'),
    },
    {
      icon: 'add_a_photo',
      title: 'Remboursement',
      subtitle: 'Demander un remboursement',
      iconBg: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      onClick: () => navigate('/claims/new'),
    },
    {
      icon: 'support_agent',
      title: 'Support',
      subtitle: 'Aide & Contact',
      iconBg: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      onClick: () => navigate('/support'),
    },
  ];

  const notificationsButton = (
    <button
      type="button"
      onClick={() => navigate('/notifications')}
      aria-label="Voir les notifications"
      className="relative flex items-center justify-center rounded-full size-10 bg-gray-50 dark:bg-white/5 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
    >
      <MaterialIcon icon="notifications" />
      <span aria-hidden="true" className="absolute top-2.5 right-2.5 size-2 bg-error rounded-full border border-white dark:border-surface-dark" />
    </button>
  );

  return (
    <AppShell rightElement={notificationsButton}>
      <div className="flex flex-col gap-5 px-4 md:px-0 pt-4 md:pt-6">
        {coverageStatus !== 'active' && (
          <CoverageBanner status={coverageStatus} message={benefitsData?.statusMessage} />
        )}

        {/* Greeting */}
        <section className="flex items-center gap-3 md:hidden">
          <div className="relative">
            <div
              className="bg-center bg-no-repeat bg-cover rounded-full size-12 border-2 border-primary bg-gray-200"
              style={{
                backgroundImage: user?.profilePictureUrl ? `url("${user.profilePictureUrl}")` : 'none'
              }}
            >
              {!user?.profilePictureUrl && (
                <div className="flex items-center justify-center w-full h-full text-gray-600 font-bold">
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </div>
              )}
            </div>
            <span aria-hidden="true" className="absolute bottom-0 right-0 size-3 bg-primary rounded-full border-2 border-white dark:border-surface-dark" />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Bienvenue 👋</p>
            <h2 className="text-gray-900 dark:text-white text-lg font-bold leading-tight tracking-tight">
              {user?.firstName} {user?.lastName}
            </h2>
          </div>
        </section>

        <h1 className="hidden md:block text-2xl font-display font-bold text-gray-900 dark:text-white">
          Bonjour {user?.firstName} 👋
        </h1>

        {/* Coverage status */}
        <section className="relative overflow-hidden rounded-2xl bg-surface-light dark:bg-surface-dark shadow-sm ring-1 ring-black/5 dark:ring-white/10 p-4">
          <div aria-hidden="true" className="absolute top-0 right-0 -mr-8 -mt-8 size-24 rounded-full bg-primary/10 blur-2xl" />
          <div className="flex flex-row justify-between items-center relative z-10">
            <div className="flex flex-col gap-1">
              {isCoverageActive && (
                <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/20 px-2.5 py-0.5 text-[10px] font-semibold text-green-800 dark:text-green-300 w-fit">
                  <MaterialIcon icon="verified_user" className="filled" style={{ fontSize: 12 }} />
                  <span>Couverture active</span>
                </div>
              )}
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {contractName}
              </h3>
              {contractEnd && (
                <p className="text-xs text-gray-500 dark:text-gray-400">Validité : {contractEnd}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate('/benefits')}
              aria-label="Voir mes garanties"
              className="bg-primary/10 p-2 rounded-lg text-primary hover:bg-primary/20 transition-colors"
            >
              <MaterialIcon icon="arrow_forward" style={{ fontSize: 24 }} />
            </button>
          </div>
        </section>

        {/* Two-column layout on desktop: card preview + quick actions */}
        <div className="grid gap-5 md:grid-cols-2">
          <button
            type="button"
            onClick={() => navigate('/card')}
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-green-600 p-4 text-white shadow-md active:scale-[0.98] transition-all group text-left"
          >
            <div aria-hidden="true" className="absolute top-0 right-0 -mr-4 -mt-4 size-20 rounded-full bg-white/20 blur-xl" />
            <div className="flex justify-between items-center relative z-10">
              <div className="flex flex-col gap-0.5">
                <p className="text-[10px] uppercase opacity-80 font-bold tracking-wider">Ma Carte Tiers Payant</p>
                <h4 className="font-bold text-base">{user?.firstName} {user?.lastName}</h4>
                <p className="text-xs opacity-90">{user?.memberNumber || 'NUM-000000'}</p>
              </div>
              <div className="bg-white p-1.5 rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                <MaterialIcon icon="qr_code_2" className="text-primary-dark" style={{ fontSize: 40 }} />
              </div>
            </div>
          </button>

          <section>
            <h2 className="text-gray-900 dark:text-white text-lg font-bold leading-tight tracking-tight mb-3">
              Accès rapides
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {quickActions.map((action, index) => (
                <QuickAction key={index} {...action} />
              ))}
            </div>
          </section>
        </div>

        {/* Last reimbursement */}
        {lastReimbursement && (
          <section>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-gray-900 dark:text-white text-lg font-bold leading-tight tracking-tight">
                Dernier remboursement
              </h2>
              <button
                type="button"
                onClick={() => navigate('/claims')}
                className="text-primary hover:text-[#25c25a] text-sm font-medium"
              >
                Tout voir
              </button>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/claims/${lastReimbursement.id}`)}
              className="flex w-full gap-4 bg-surface-light dark:bg-surface-dark px-4 py-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 items-center hover:border-primary/30 transition-colors text-left"
            >
              <div className="flex items-center justify-center rounded-lg bg-green-50 dark:bg-green-900/20 shrink-0 size-12 text-green-700 dark:text-green-400">
                <MaterialIcon icon="payments" />
              </div>
              <div className="flex flex-1 flex-col justify-center">
                <p className="text-gray-900 dark:text-white text-base font-bold leading-normal">
                  {lastReimbursement.providerName}
                </p>
                <p className="text-gray-500 dark:text-gray-400 text-xs font-normal leading-normal">
                  {new Date(lastReimbursement.reimbursementDate ?? lastReimbursement.updatedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-primary font-bold text-base">
                  +{formatCurrency(lastReimbursement.reimbursedAmount).replace('XOF', '').trim()} F
                </p>
                <p className="text-[10px] text-gray-400 uppercase font-medium">Remboursé</p>
              </div>
            </button>
          </section>
        )}

        {/* Educational banner */}
        <section className="relative overflow-hidden rounded-xl bg-[#102216] p-4 text-white shadow-md mb-6">
          <div aria-hidden="true" className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-primary opacity-20 blur-xl" />
          <div aria-hidden="true" className="absolute -left-4 -bottom-4 h-20 w-20 rounded-full bg-blue-500 opacity-20 blur-xl" />
          <div className="relative z-10 flex gap-4 items-center">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <MaterialIcon icon="lightbulb" className="text-primary" style={{ fontSize: 14 }} />
                <p className="text-xs font-medium text-primary uppercase tracking-wider">Astuce</p>
              </div>
              <h3 className="text-base font-bold leading-tight mb-1">
                Comment fonctionne le tiers payant ?
              </h3>
              <p className="text-gray-300 text-xs leading-relaxed">
                Comprendre vos avantages en moins d'une minute.
              </p>
            </div>
            <button
              type="button"
              aria-label="Lancer la vidéo d'explication du tiers payant"
              className="shrink-0 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-lg p-2 transition-colors"
            >
              <MaterialIcon icon="play_arrow" />
            </button>
          </div>
        </section>
      </div>
    </AppShell>
  );
};

export default DashboardPage;
